import { type ReactNode, useEffect, useRef } from "react";
import * as Direction from "@radix-ui/react-direction";
import { useLandingPageLanguage } from "./LandingPageLanguageProvider";
import { useLandingPageTheme } from "./LandingPageThemeProvider";

interface LandingPageDirectionProviderProps {
  children: ReactNode;
  // Optional prop to explicitly control isolation mode
  forceContainerOnly?: boolean;
}

/**
 * Direction provider specifically for the landing page
 * This manages RTL/LTR direction separately from the main app
 *
 * Behavior:
 * - Standalone routes (/site, /u/:siteId, /invite/preview/:token): Sets document-level direction
 * - Embedded contexts (EditPage): Uses container-only isolation
 * - forceContainerOnly prop: Forces container-only mode regardless of route
 */
export function LandingPageDirectionProvider({
  children,
  forceContainerOnly = false,
}: LandingPageDirectionProviderProps) {
  const { currentLangConfig } = useLandingPageLanguage();
  const containerRef = useRef<HTMLDivElement>(null);

  // Use the theme hook - it has built-in fallback handling
  const { isDarkMode } = useLandingPageTheme();

  // Get direction from current language config
  const direction = currentLangConfig?.direction || "ltr";
  const langCode = currentLangConfig?.code || "en";

  // Safe way to detect if we're on a standalone landing page route
  // Uses window.location as fallback since useLocation requires Router context
  const isStandaloneLandingPage = (() => {
    if (forceContainerOnly) return false;

    try {
      // Try to get the current pathname safely
      if (typeof window !== "undefined") {
        const pathname = window.location.pathname;
        // Treat the following as standalone landing page environments:
        // - Marketing/public landing page routes we explicitly render standalone
        // - Public site by ID (/u/:siteId)
        // - Invitation preview (/invite/preview/:token)
        // - Subdomains app root path ("/") which serves tenant sites on their own origin
        return (
          pathname === "/site" ||
          pathname === "/" ||
          pathname.startsWith("/u/") ||
          pathname.startsWith("/invite/preview/")
        );
      }
      return false;
    } catch {
      // If we can't detect the route, default to container-only isolation
      return false;
    }
  })();

  // Update the landing page container's direction
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.setAttribute("dir", direction);
      containerRef.current.setAttribute("lang", langCode);
      containerRef.current.style.direction = direction;
    }

    // Only set document direction for standalone landing pages
    // For embedded landing pages (like in EditPage), use container-only isolation
    if (isStandaloneLandingPage) {
      // Set document direction for standalone landing page isolation
      document.documentElement.dir = direction;
      document.documentElement.style.direction = direction;
      // Also set the HTML lang attribute for correct semantics and browser behavior
      document.documentElement.lang = langCode;

      // Store the original direction to restore later
      const originalDir =
        document.documentElement.getAttribute("data-original-dir");
      const originalLang =
        document.documentElement.getAttribute("data-original-lang");
      if (!originalDir) {
        document.documentElement.setAttribute(
          "data-original-dir",
          document.documentElement.dir || "ltr"
        );
      }
      if (!originalLang) {
        document.documentElement.setAttribute(
          "data-original-lang",
          document.documentElement.lang || "en"
        );
      }

      return () => {
        // Restore original direction when standalone landing page unmounts
        const storedOriginalDir =
          document.documentElement.getAttribute("data-original-dir");
        const storedOriginalLang =
          document.documentElement.getAttribute("data-original-lang");
        if (storedOriginalDir) {
          document.documentElement.dir = storedOriginalDir;
          document.documentElement.style.direction = storedOriginalDir;
          document.documentElement.removeAttribute("data-original-dir");
        } else {
          document.documentElement.dir = "ltr";
          document.documentElement.style.direction = "ltr";
        }
        if (storedOriginalLang) {
          document.documentElement.lang = storedOriginalLang;
          document.documentElement.removeAttribute("data-original-lang");
        } else {
          document.documentElement.lang = "en";
        }
      };
    }
    // For embedded landing pages, no cleanup needed
  }, [direction, langCode, isStandaloneLandingPage]);

  return (
    <Direction.Provider dir={direction}>
      <div
        key={`${direction}-${isDarkMode}`} // Force re-render when direction or dark mode changes
        ref={containerRef}
        className={`landing-page-isolated ${!isStandaloneLandingPage ? "edit-mode-container" : ""} ${isDarkMode ? "dark" : ""}`}
        dir={direction}
        lang={langCode}
        style={{
          direction,
          // Ensure complete isolation from parent styles
          position: "relative",
          width: "100%",
          height: "100%",
          minHeight: isStandaloneLandingPage ? "100vh" : "100%",
          isolation: "isolate",
          // Reset any potential inherited text direction properties
          textAlign: direction === "rtl" ? "right" : "left",
          unicodeBidi: "normal",
        }}
      >
        {children}
      </div>
    </Direction.Provider>
  );
}
